import { cs } from 'date-fns/locale';
import { MonthYear } from './DayWeekPickerGroup';
import React, { useMemo, useState } from 'react';
import {
  addDays,
  addMonths,
  endOfMonth,
  isSameDay,
  Locale,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { CalendarEventProps } from './Calendar';

export type UseCalendarData<ID> = {
  events?: CalendarEventProps<ID>[];
};

export type UseCalendarConfig = {
  dayWeekPickersCount?: number;
  locale?: Locale;
  days?: Date[];
  monthYear?: MonthYear;
};

export type UseCalendarProps = {
  days: Date[];
  setDays: (days: Date[]) => void;
  calendarProps: {
    days: Date[];
    setDays: (days: Date[]) => void;
    locale: Locale;
    onWeekChange: (weekDays: Date[]) => void;
  };
  dayWeekPickerGroupProps: {
    days: Date[];
    setDays: (days: Date[]) => void;
    locale: Locale;
    monthYear: MonthYear;
    pickersCount: number;
    highlightedDays?: Date[];
  };
};

export const useCalendar = <ID>(
  calendarData?: UseCalendarData<ID>,
  config?: UseCalendarConfig
): UseCalendarProps => {
  const locale = config?.locale || cs;
  const pickersCount = config?.dayWeekPickersCount || 2;

  const [days, setDays] = useState<Date[]>(() => {
    if (config?.days) {
      return config?.days;
    } else {
      const firstMonday = startOfWeek(new Date(), { weekStartsOn: 1 });
      return Array.from({ length: 7 }, (_, i) => addDays(firstMonday, i));
    }
  });

  const [monthYear, setMonthYear] = React.useState<MonthYear>(() => {
    if (config?.monthYear) {
      return config.monthYear;
    } else {
      return {
        month: new Date().getMonth(),
        year: new Date().getFullYear(),
      };
    }
  });

  const handleWeekChange = (weekDays: Date[]) => {
    const firstDayOfWeek = weekDays[0];

    const showForDate = new Date(monthYear.year, monthYear.month);
    const start = startOfWeek(startOfMonth(showForDate), { locale });
    const end = startOfWeek(endOfMonth(addMonths(showForDate, 1)), { locale });

    if (+firstDayOfWeek < +start) {
      setMonthYear({
        month: firstDayOfWeek.getMonth(),
        year: firstDayOfWeek.getFullYear(),
      });
    } else if (+firstDayOfWeek > +end) {
      const buttomDayWeekPicker = addMonths(
        firstDayOfWeek,
        -(pickersCount - 1)
      );
      setMonthYear({
        month: buttomDayWeekPicker.getMonth(),
        year: buttomDayWeekPicker.getFullYear(),
      });
    }
  };

  const highlightedDays = useMemo(() => {
    let result: Date[] = [];

    // Vrácení distinct dnů ze startDate eventů
    if (calendarData?.events) {
      calendarData.events.forEach((e) => {
        e.events.forEach((event) => {
          if (!result.find((r) => isSameDay(r, event.startDate))) {
            result.push(event.startDate);
          }
        });
      });
    }

    return result;
  }, [calendarData?.events]);

  return {
    days,
    setDays,
    calendarProps: {
      days,
      setDays,
      locale,
      onWeekChange: handleWeekChange,
    },
    dayWeekPickerGroupProps: {
      days,
      setDays,
      pickersCount,
      locale,
      monthYear,
      highlightedDays,
    },
  };
};
